import queryString from "query-string";
import constants from "../../../tools/constants";
import BatchType from "../../../types/Batch";
import { BATCH_STATUS } from "../../batches/components/FilteringBatchesDialog";
import { batchMapper, projectMapper } from "./mappers";
import { fetchOnce } from "./fetchOnce";
import { HomepageProject } from "../widgets/types";

export const ASSIGNED_BATCHES_DISPLAY_SIZE = 5;
export const ACTIVE_BATCHES_DISPLAY_SIZE = 5;
export const RECENT_PROJECTS_SIZE = 5;

export const buildAssignedBatchesPath = (userId: string | number) => {
  const params = queryString.stringify({
    userId,
    size: ASSIGNED_BATCHES_DISPLAY_SIZE,
    sort: "createdDate,desc",
    page: 0,
  });
  return `${constants.paths.BATCHES_PATH}?${params}`;
};

export const buildActiveBatchesPath = (userId: string | number) => {
  const params = queryString.stringify({
    userId,
    batchState: BATCH_STATUS.IN_PROGRESS.name,
    size: ACTIVE_BATCHES_DISPLAY_SIZE,
    sort: "createdDate,desc",
    page: 0,
  });
  return `${constants.paths.BATCHES_PATH}?${params}`;
};

export const buildHistoryBatchPath = (batchId: unknown) => {
  const params = queryString.stringify({
    batchIds: batchId,
    size: 1,
    page: 0,
  });
  return `${constants.paths.BATCHES_PATH}?${params}`;
};

export const buildRecentProjectsPath = (userId: string | number) => {
  const params = queryString.stringify({
    userId,
    sort: "createdDate,desc",
    size: RECENT_PROJECTS_SIZE,
    page: 0,
  });
  return `${constants.paths.USER_PROJECTS_PATH}?${params}`;
};

export const selectBatches = (raw: unknown): BatchType[] =>
  ((raw as any).content || raw).map(batchMapper);

export const selectProjects = (raw: unknown): HomepageProject[] =>
  ((raw as any).content || raw).map(projectMapper);

export const fetchBatchesAtPath = (
  path: string,
  appFetch: (url: string) => Promise<any>,
): Promise<BatchType[]> =>
  fetchOnce(path, () => appFetch(path)).then(selectBatches);

export const fetchFirstBatchAtPath = async (
  path: string,
  appFetch: (url: string) => Promise<any>,
): Promise<BatchType | null> => {
  const batches = await fetchBatchesAtPath(path, appFetch);
  return batches.length > 0 ? batches[0] : null;
};

export const loadResumeBatch = (
  userId: string | number,
  appFetch: (url: string) => Promise<any>,
  getHistory: (userId: string) => unknown[],
): Promise<BatchType | null> => {
  const historyIds = getHistory(String(userId));
  const lastHistoryId =
    historyIds.length > 0 ? String(historyIds[historyIds.length - 1]) : "none";

  return fetchOnce(`homepage:resume:${userId}:${lastHistoryId}`, async () => {
    if (lastHistoryId !== "none") {
      const historyPath = buildHistoryBatchPath(lastHistoryId);
      const fromHistory = await fetchFirstBatchAtPath(historyPath, appFetch);
      if (fromHistory) {
        return fromHistory;
      }
    }

    const assignedPath = buildAssignedBatchesPath(userId);
    const assignedBatches = await fetchBatchesAtPath(assignedPath, appFetch);
    return assignedBatches.length > 0 ? assignedBatches[0] : null;
  });
};
